/**
 * Debrief for Control: the tables side by side, calibration, the computed
 * prompts and the notes taken during the shift (DESIGN §10.5).
 */
import { useEffect, useState } from 'react';
import type { CalibrationRow, DebriefView, TableTile } from '@nightshift/engine/client';
import { api } from '../net/api.ts';
import { Fold, ROLE_TITLE } from '../ui/bits.tsx';
import type { Note, Timeline } from './wire.ts';

export const AXES: { k: keyof DebriefView['tables'][number]['dashboard']; label: string; good?: 'high' | 'low'; layer?: 'staff' | 'vents' | 'blood' }[] = [
  { k: 'saved', label: 'Saved', good: 'high' },
  { k: 'harmed', label: 'Harmed', good: 'low' },
  { k: 'died', label: 'Died', good: 'low' },
  { k: 'handedOver', label: 'Handed over at 0700' },
  { k: 'early', label: 'Sent home early' },
  { k: 'bounceBacks', label: 'Bounced back', good: 'low' },
  { k: 'diverted', label: 'Diverted away' },
  { k: 'accepted', label: 'Received from elsewhere' },
  { k: 'scans', label: 'Scans' },
  { k: 'theatre', label: 'Theatre cases' },
  { k: 'icuBedTurns', label: 'ICU bed-turns' },
  { k: 'calls', label: 'Calls for help' },
  { k: 'plannedEscalations', label: 'Planned escalations' },
  { k: 'crisisEscalations', label: 'Crisis escalations' },
  { k: 'lateEscalations', label: 'Late escalations', good: 'low' },
  { k: 'idleCapacity', label: 'Booked turns not used', good: 'low' },
  { k: 'resources', label: 'Total resources used' },
  { k: 'overflowDelays', label: 'Discharges delayed by workload', good: 'low' },
  { k: 'unstaffedPatientTurns', label: 'Unstaffed patient-turns', good: 'low', layer: 'staff' },
  { k: 'ventShortTurns', label: 'Turns short of a ventilator', good: 'low', layer: 'vents' },
  { k: 'bloodUsed', label: 'Blood units used', layer: 'blood' },
];

/** The rows that answer "how did the night go"; everything else is one tap away. */
const KEY_AXES = new Set<(typeof AXES)[number]['k']>(['saved', 'harmed', 'died']);

function AxisRow({ a, d }: { a: (typeof AXES)[number]; d: DebriefView }) {
  const vals = d.tables.map((t) => t.dashboard[a.k] as number);
  const allSame = vals.every((x) => x === vals[0]);
  const complete = vals.every(value => Number.isFinite(value));
  const best = allSame || !complete ? null : a.good === 'high' ? Math.max(...vals) : a.good === 'low' ? Math.min(...vals) : null;
  return (
    <tr>
      <th>{a.label}</th>
      {vals.map((v, i) => (
        <td key={i} className={best !== null && v === best && d.tables.length > 1 ? 'best' : ''}>
          {v ?? '—'}
        </td>
      ))}
    </tr>
  );
}

export function DebriefPanel({ d, tables, sessionId, readonly, layers, endedAt }: { d: DebriefView; tables: TableTile[]; sessionId: string; readonly: boolean; layers: Record<'staff' | 'vents' | 'blood', boolean>; endedAt: string | null }) {
  const [notes, setNotes] = useState<Note[]>([]);
  useEffect(() => {
    if (readonly) return;
    api<Timeline>(`/api/sessions/${sessionId}/timeline?min=3`)
      .then((t) => setNotes(t.notes))
      .catch(() => setNotes([]));
  }, [sessionId, readonly]);
  const anyLayer = (l?: 'staff' | 'vents' | 'blood') => !l || layers[l];
  const totals = d.tables.reduce((a, t) => ({ saved: a.saved + t.dashboard.saved, harmed: a.harmed + t.dashboard.harmed, died: a.died + t.dashboard.died }), { saved: 0, harmed: 0, died: 0 });
  return (
    <div className="debrief">
      <header className="db-head">
        <h1 className="display">{endedAt ? `Control ended the shift at ${endedAt}.` : '0700. The day team has the building.'}</h1>
        {!readonly && <a className="btn btn-small" href={`/control/s/${sessionId}/print?table=all`} target="_blank" rel="noopener">Print debrief</a>}
        <p className="db-totals">
          <span>
            <b className="display">{totals.saved}</b> saved
          </span>
          <span>
            <b className="display">{totals.harmed}</b> harmed
          </span>
          <span>
            <b className="display">{totals.died}</b> died
          </span>
          <span className="muted">across {d.tables.length} table{d.tables.length > 1 ? 's' : ''}</span>
        </p>
      </header>

      <section className="db-compare" aria-label="Tables compared">
        <table className="fac-table compare">
          <thead>
            <tr>
              <th />
              {d.tables.map((t) => (
                <th key={t.no}>Table {t.no}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {AXES.filter((a) => KEY_AXES.has(a.k)).map((a) => (
              <AxisRow key={a.k} a={a} d={d} />
            ))}
            <tr>
              <th>Objectives met</th>
              {d.tables.map((t) => (
                <td key={t.no}>
                  {t.objectives.filter((o) => o.achieved).length} of {t.objectives.length}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
        <Fold title="All measures" hint={`${AXES.filter((a) => !KEY_AXES.has(a.k) && anyLayer(a.layer)).length} more`} testId="all-measures">
          <table className="fac-table compare">
            <tbody>
              {AXES.filter((a) => !KEY_AXES.has(a.k) && anyLayer(a.layer)).map((a) => (
                <AxisRow key={a.k} a={a} d={d} />
              ))}
            </tbody>
          </table>
        </Fold>
      </section>

      <div className="db-split">
        <section className="db-cal" aria-label="Calibration">
          <h2 className="display">Were the numbers honest?</h2>
          <Calibration rows={d.calibration} />
        </section>
        <section className="db-notes" aria-label="Notes">
          <h2 className="display">Your notes</h2>
          {notes.length === 0 && <p className="muted">No notes taken. Next time, jot what you see in the rail on the right.</p>}
          <ul className="plain">
            {notes.map((n) => (
              <li key={n.id} className="note">
                <button
                  className={`star ${n.starred ? 'on' : ''}`}
                  aria-pressed={!!n.starred}
                  aria-label={n.starred ? 'Remove from the projector' : 'Show on the projector'}
                  onClick={async () => {
                    await api(`/api/notes/${n.id}/star`, { body: { starred: !n.starred } }).catch(() => {});
                    setNotes(notes.map((x) => (x.id === n.id ? { ...x, starred: x.starred ? 0 : 1 } : x)));
                  }}
                >
                  ★
                </button>
                <span>
                  {n.table_no ? <span className="muted">Table {n.table_no}: </span> : null}
                  {n.text}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="db-tables" aria-label="Per table">
        {d.tables.map((t) => {
          const tile = tables.find((x) => x.no === t.no);
          return (
            <article key={t.no} className="db-table">
              <h2 className="display">Table {t.no}</h2>
              <ul className="db-obj">
                {t.objectives.map((o) => (
                  <li key={o.role} className={o.achieved ? 'won' : ''}>
                    <span className={`spec-dot spec-${o.role}`} />
                    <span>
                      <strong>{ROLE_TITLE[o.role]}</strong>
                      {tile?.seats.find((s) => s.role === o.role)?.name ? <span className="muted"> {tile?.seats.find((s) => s.role === o.role)?.name}</span> : null}: {o.title}, {o.achieved ? 'met' : 'not met'}
                      <span className="muted small"> {o.detail}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <h3 className="sec-title">Ask the table</h3>
              <ul className="db-prompts">
                {t.prompts.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </article>
          );
        })}
      </section>
    </div>
  );
}

/** Compare printed certainty against drawn cards and the whole deck. */
export function Calibration({ rows }: { rows: CalibrationRow[] }) {
  if (rows.length === 0) return <p className="muted">No cards drawn.</p>;
  const W = 320;
  const H = 180;
  const x = (box: number) => 30 + ((box - 40) / 60) * (W - 50);
  const y = (pct: number) => H - 24 - (pct / 100) * (H - 40);
  return (
    <figure className="cal">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Calibration: printed certainty against how often the diagnosis was right">
        {[0, 50, 100].map((g) => (
          <g key={g}>
            <line x1={28} x2={W - 10} y1={y(g)} y2={y(g)} className="cal-grid" />
            <text x={4} y={y(g) + 4} className="cal-ax">
              {g}%
            </text>
          </g>
        ))}
        <line x1={x(40)} y1={y(40)} x2={x(100)} y2={y(100)} className="cal-ideal" />
        {rows.map((r) => {
          const drawn = r.drawn ? (r.drawnRight / r.drawn) * 100 : null;
          const deck = r.deck ? (r.deckRight / r.deck) * 100 : null;
          return (
            <g key={r.box}>
              <text x={x(r.box)} y={H - 6} textAnchor="middle" className="cal-ax">
                {r.box}
              </text>
              {deck !== null && <rect x={x(r.box) - 9} y={y(deck)} width={8} height={H - 24 - y(deck)} className="cal-deck" />}
              {drawn !== null && <rect x={x(r.box) + 1} y={y(drawn)} width={8} height={H - 24 - y(drawn)} className="cal-drawn" />}
            </g>
          );
        })}
      </svg>
      <figcaption className="small muted">
        <span className="key deck" /> whole deck <span className="key drawn" /> drawn cards. The diagonal is an honest card: a 70 should be right seven times in ten.
      </figcaption>
      <table className="fac-table small">
        <thead>
          <tr>
            <th>Printed</th>
            <th>Drawn, right</th>
            <th>Deck, right</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.box}>
              <td>{r.box}%</td>
              <td>
                {r.drawnRight} of {r.drawn}
              </td>
              <td>
                {r.deckRight} of {r.deck}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
